import {
  createContext,
  useContext,
  PropsWithChildren,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { DateRange } from "@/components/shared/date-range-picker/DateRangePicker";
import {
  FilterValue,
  StringSlicerType,
  NumericSlicerType,
  StringSlicer,
  NumericSlicer,
  NumericSlicerPreset,
  isNumericSlicerPreset,
  GroupByType,
  componseSlicers,
  parseSlicers,
  groupByToSliceType,
  isGroupByType,
  enhanceFilterValue,
  isViewType,
} from "./FilterContext.model";
import { useExploreTracking } from "../useExploreTracking";
import dayjs from "dayjs";

type FilterContextState = FilterValue;

interface FilterContextActions {
  resetFilter: (newFilterValue: FilterContextState) => void;
  setGroupBy: (newGroupBy: GroupByType) => void;
  setDateRange: (newDateRange: DateRange) => void;
  setStringSlicer: (key: StringSlicerType, value: StringSlicer) => void;
  removeStringSlicer: (key: StringSlicerType) => void;
  setNumericSlicer: (
    key: NumericSlicerType,
    value: NumericSlicer | NumericSlicerPreset
  ) => void;
  removeNumericSlicer: (key: NumericSlicerType) => void;
  diveInto: (from: GroupByType, to: GroupByType, value: string) => void;
  diveIntoTickets: (value: string) => void;
  diveIntoConversations: (value: string) => void;
}

type FilterContextValue = FilterContextState & FilterContextActions;

const FilterContext = createContext<FilterContextValue>(null!);

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilter must be used within a FilterProvider");
  }
  return context;
};

interface FilterProviderProps {
  defaultValue?: {
    groupBy?: GroupByType;
    dateRange?: DateRange;
  };
  source?: "explore" | "explore-conversation" | "insights" | "evaluations";
}

export function buildFilterSearchParams(state: FilterValue) {
  const {
    groupBy,
    dateRange,
    stringSliceBy,
    numericSliceBy,
    view,
    mainSliceBy,
  } = state;

  const componsedSlicers = componseSlicers({
    stringSliceBy,
    numericSliceBy,
    mainSliceBy,
  });

  const newSearchParams = new URLSearchParams([
    ...(groupBy != null ? [["by", groupBy]] : []),
    ...(dateRange.dateFrom != null ? [["from", dateRange.dateFrom]] : []),
    ...(dateRange.dateTo != null ? [["to", dateRange.dateTo]] : []),
    ...(view != null ? [["view", view]] : []),
    ...componsedSlicers,
  ]);

  return newSearchParams;
}

function initializeState(
  searchParams: URLSearchParams,
  defaultValue?: FilterProviderProps["defaultValue"]
): FilterContextState {
  const groupBy = searchParams.get("by");
  const dateRangeFrom =
    searchParams.get("from") ??
    dayjs().subtract(14, "days").format("YYYY-MM-DD");
  const dateRangeTo = searchParams.get("to") ?? dayjs().format("YYYY-MM-DD");

  const { stringSliceBy, numericSliceBy } = parseSlicers(searchParams);
  const view = searchParams.get("view");

  const state: FilterContextState = {
    ...(groupBy != null && isGroupByType(groupBy)
      ? { groupBy }
      : defaultValue?.groupBy != null
      ? { groupBy: defaultValue.groupBy }
      : {}),
    ...{
      dateRange: { dateFrom: dateRangeFrom ?? "", dateTo: dateRangeTo ?? "" },
    },
    ...(stringSliceBy != null ? { stringSliceBy } : {}),
    ...(numericSliceBy != null ? { numericSliceBy } : {}),
    ...(view != null && isViewType(view) ? { view } : {}),
  };

  return enhanceFilterValue(state);
}

export function FilterProvider({
  children,
  defaultValue,
  source = "explore",
}: PropsWithChildren<FilterProviderProps>) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackFilterApplied } = useExploreTracking();

  const [state, setState] = useState<FilterContextState>(() =>
    initializeState(searchParams, defaultValue)
  );

  const actions = useMemo<FilterContextActions>(() => {
    return {
      resetFilter: (newFilterValue: FilterContextState) => {
        setState(newFilterValue);
      },
      setGroupBy: (newGroupBy: GroupByType) => {
        setState((prevState) => ({
          groupBy: newGroupBy,
          dateRange: prevState.dateRange,
        }));
      },
      setDateRange: (newDateRange: DateRange) => {
        setState((prevState) => ({ ...prevState, dateRange: newDateRange }));
      },
      setStringSlicer: (key: StringSlicerType, value: StringSlicer) => {
        setState((prevState) => {
          const newStringSlicers = (
            prevState.stringSliceBy?.and.filter(
              (slicer) => slicer.type !== key
            ) ?? []
          ).concat(value);

          return {
            ...prevState,
            stringSliceBy: { and: newStringSlicers },
          };
        });
      },
      removeStringSlicer: (key: StringSlicerType) => {
        setState(({ stringSliceBy, ...restPrevState }) => {
          const newStringSlicers =
            stringSliceBy?.and.filter((slicer) => slicer.type !== key) ?? [];

          return {
            ...restPrevState,
            ...(newStringSlicers.length > 0
              ? { stringSliceBy: { and: newStringSlicers } }
              : {}),
          };
        });
      },
      setNumericSlicer: (
        key: NumericSlicerType,
        value: NumericSlicer | NumericSlicerPreset
      ) => {
        setState((prevState) => {
          const newNumericSlicers = (
            prevState.numericSliceBy?.and.filter((slicer) => {
              if (isNumericSlicerPreset(slicer)) {
                return slicer.split("_")[0] !== key;
              }
              return slicer.type !== key;
            }) ?? []
          ).concat(value);

          return {
            ...prevState,
            numericSliceBy: { and: newNumericSlicers },
          };
        });
      },
      removeNumericSlicer: (key: NumericSlicerType) => {
        setState(({ numericSliceBy, ...restPrevState }) => {
          const newNumericSlicers =
            numericSliceBy?.and.filter((slicer) => {
              if (isNumericSlicerPreset(slicer)) {
                return slicer.split("_")[0] !== key;
              }
              return slicer.type !== key;
            }) ?? [];

          return {
            ...restPrevState,
            ...(newNumericSlicers.length > 0
              ? { numericSliceBy: { and: newNumericSlicers } }
              : {}),
          };
        });
      },
      diveInto: (from: GroupByType, to: GroupByType, value: string) => {
        setState((prevState) => {
          if (from == null || groupByToSliceType[from] == null) {
            return prevState;
          }

          return {
            ...prevState,
            groupBy: to,
            ...(prevState.mainSliceBy != null
              ? {
                  stringSliceBy: {
                    and: [
                      {
                        type: prevState.mainSliceBy.type,
                        operation: "and",
                        values: [prevState.mainSliceBy],
                      },
                      ...(prevState.stringSliceBy?.and ?? []),
                    ],
                  },
                }
              : {}),
            mainSliceBy: {
              type: groupByToSliceType[from],
              operation: "eq",
              value,
            },
          };
        });
      },
      diveIntoTickets: (value: string) => {
        setState((prevState) => {
          const { groupBy } = prevState;

          if (groupBy == null || groupByToSliceType[groupBy] == null) {
            return prevState;
          }

          const type = groupByToSliceType[groupBy];

          return {
            ...prevState,
            view: "tickets",
            ...(prevState.mainSliceBy != null
              ? {
                  stringSliceBy: {
                    and: [
                      {
                        type: prevState.mainSliceBy.type,
                        operation: "and",
                        values: [prevState.mainSliceBy],
                      },
                      ...(prevState.stringSliceBy?.and ?? []),
                    ],
                  },
                }
              : {}),
            mainSliceBy: {
              type,
              operation: "eq",
              value,
            },
          };
        });
      },
      diveIntoConversations: (value: string) => {
        setState((prevState) => {
          const { groupBy } = prevState;

          if (groupBy == null || groupByToSliceType[groupBy] == null) {
            return prevState;
          }

          const type = groupByToSliceType[groupBy];

          return {
            ...prevState,
            view: "conversations",
            ...(prevState.mainSliceBy != null
              ? {
                  stringSliceBy: {
                    and: [
                      {
                        type: prevState.mainSliceBy.type,
                        operation: "and",
                        values: [prevState.mainSliceBy],
                      },
                      ...(prevState.stringSliceBy?.and ?? []),
                    ],
                  },
                }
              : {}),
            mainSliceBy: {
              type,
              operation: "eq",
              value,
            },
          };
        });
      },
    };
  }, []);

  useEffect(() => {
    setSearchParams(buildFilterSearchParams(state));

    trackFilterApplied('filter_changed', state, source);
  }, [source, JSON.stringify(state), trackFilterApplied]);

  useEffect(() => {
    setState((prevState) => {
      const prevSearchParams = buildFilterSearchParams(prevState);
      const hasChanged =
        prevSearchParams.toString() !== searchParams.toString();

      return hasChanged
        ? initializeState(searchParams, defaultValue)
        : prevState;
    });
  }, [searchParams]);

  return (
    <FilterContext.Provider value={{ ...state, ...actions }}>
      {children}
    </FilterContext.Provider>
  );
}
